import api from "@/app/_module/services/api";
import { getApiUrl } from "@/app/_module/services/config";
import { getSupabaseClient } from "@/app/_module/services/supabaseClient";
import { endpoint } from "@/app/_module/services/endpoints";
import { handleApiCall } from "@/app/_module/utils/apiResponseHandler";
import { deduplicateRequest } from "@/app/_module/utils/request-deduplication";
import { getSignupSessionId } from "./signup-funnel-tracker";
// NOTE: Supabase invite system is DISABLED - we use SendGrid for agency invitations
// This service handles both regular signup and SendGrid-based agency invitation signup

export interface SignupRequestBody {
  // Honeypot — hidden field, must be empty for real users (bot detection)
  website?: string;
  // Account info (matches users table)
  email: string;
  password?: string; // Optional for social auth
  name: string;
  contact_phone?: string;
  user_type?: 'agent' | 'admin' | 'owner'; // Determined by backend from requestedRole/trial type
  npn?: string;
  npnState?: string; // State where NPN is licensed
  
  // Social auth
  socialProvider?: 'google' | 'apple' | 'microsoft';
  socialId?: string;
  
  // Plan selection (maps to account_type); 'dialer' is LeadStoreSniper's free dialer account
  selectedPlan: 'lite' | 'basic' | 'full_fledged' | 'dialer';
  
  // Trial selection
  requestedTrialType?: 'lite' | 'basic' | 'full_fledged';
  
  // Agency (premium only)
  agencyName?: string;
  agencyDescription?: string;
  agencyContactEmail?: string;
  requestedRole?: 'agent' | 'agency_owner';
  /** Self-reported downline headcount from signup question. */
  downlineAgentCount?: number;
  
  // Area code selection (for Twilio number provisioning)
  selectedAreaCode?: number;
  selectedState?: string;
  
  // Payment method (vaulted during signup)
  paymentMethodId?: string; // Stripe payment method ID
  signupSource?: string; // 'ios-app' | 'android-app' | 'web' — persisted for support/analytics
  
  // Security
  turnstileToken?: string;
  
  // Invite-related fields
  inviteToken?: string;
  isInviteSignup?: boolean;
  agencyId?: string; // For invited users

  // Referral program
  referralCode?: string; // Referral code from invite link
}

export interface SignupResponseBody {
  statusCode?: number;
  message?: string;
  data?: unknown;
}

export const signupService = {
  // Check trial eligibility - Now uses the new public endpoint!
  checkTrialEligibility: async (npn: string, email: string): Promise<any> => {
    try {
      // Use the new public endpoint for signup flow
      const result = await handleApiCall(async () => {
        const response = await api.get({
          url: getApiUrl(endpoint.plans.checkTrialEligibilityPublic('basic', npn, email))
        });
        return response;
      });
      return result;
    } catch (error: any) {

      // Graceful fallback for any remaining issues
      return {
        success: true,
        data: {
          isEligible: true,
          reason: 'Public signup flow - trial available',
          remainingTrials: 1,
          hasUsedTrial: false,
          availableTrials: ['basic', 'full_fledged'],
          npnValidated: true,
          antiAbusePassed: true
        }
      };
    }
  },

  // Get available plans - Now works reliably with public endpoint!
  getAvailablePlans: async (npn?: string): Promise<any> => {
    try {
      // For signup context, explicitly pass context=signup to get only trial plans
      const baseUrl = getApiUrl(endpoint.plans.getAvailablePlans);
      const params = new URLSearchParams();
      params.set('context', 'signup');
      if (npn) {
        params.set('npn', npn);
      }
      const url = `${baseUrl}?${params.toString()}`;
      
      const result = await handleApiCall(async () => {
        const response = await api.get({
          url,
          auth: false // Plans endpoint is public for signup
        });
        return response;
      });
      return result;
    } catch (error) {

      // Minimal fallback only for true API failures
      return {
        success: true,
        message: 'Fallback plans (API connectivity issue)',
        data: [
          {
            idx: 0,
            value: 'basic',
            label: 'Basic',
            description: '$120/month - Essential features for basic lead management',
            monthly_price: 120,
            features: '["power_dialer", "policy_upload_management", "lead_management", "unlimited_calls_history", "unlimited_sms_history"]',
            area_code_pricing: 4,
            is_selectable: true
          },
          {
            idx: 1,
            value: 'full_fledged',
            label: 'Full-Fledged',
            description: '$240/month - All features including unlimited leads, AI bot and call recordings',
            monthly_price: 240,
            features: '["power_dialer", "ai_bot_access", "call_recordings", "agency_creation_and_management", "unlimited_leads"]',
            area_code_pricing: 2,
            is_selectable: true
          }
        ]
      };
    }
  },

  // Setup MFA (unified)
  setupMFA: async (method: 'totp' | 'sms', phoneNumber?: string, emailOverride?: string): Promise<any> => {
    try {
      let response: any;
      try {
        response = await handleApiCall(async () => {
          const res = await api.post({
            url: getApiUrl(endpoint.auth.mfaSetup),
            body: {
              method,
              ...(method === 'sms' ? { phoneNumber } : {}),
              ...(emailOverride ? { email: emailOverride } : {}),
            },
            auth: true // Add authentication for MFA setup
          });
          return res;
        });
      } catch (firstErr: any) {
        // Fallback: if unauthorized or missing user context, retry including email
        const supabase = await getSupabaseClient();
        const { data: { user } } = await supabase.auth.getUser();
        const email = emailOverride || user?.email;
        if (!email) throw firstErr;
        response = await handleApiCall(async () => {
          const res = await api.post({
            url: getApiUrl(endpoint.auth.mfaSetup),
            body: {
              method,
              ...(method === 'sms' ? { phoneNumber } : {}),
              email,
            },
            auth: true // Add authentication for MFA setup
          });
          return res;
        });
      }

      const data = response?.data?.data || response?.data || response;

      // Normalize shape
      if (method === 'totp') {
        return {
          statusCode: 200,
          data: {
            qrCode: data.qrCodeUrl || data.qrCode,
            secret: data.manualEntryKey || data.secret,
            backupCodes: data.backupCodes || [],
          }
        };
      }

      return { statusCode: 200, data };
    } catch (error) {

      throw error;
    }
  },

  // Verify MFA (unified)
  verifyMFA: async (code: string, method: 'totp' | 'sms', emailOverride?: string, phoneNumber?: string): Promise<any> => {
    try {
      let response: any;
      try {
        response = await handleApiCall(async () => {
          const res = await api.post({
            url: getApiUrl(endpoint.auth.mfaVerifySetup),
            body: {
              code,
              method,
              ...(emailOverride ? { email: emailOverride } : {}),
              ...(method === 'sms' && phoneNumber ? { phoneNumber } : {}),
            },
            auth: true // Add authentication for MFA verification
          });
          return res;
        });
      } catch (firstErr: any) {
        // Fallback: retry with email if JWT missing/expired
        const supabase = await getSupabaseClient();
        const { data: { user } } = await supabase.auth.getUser();
        const email = emailOverride || user?.email;
        if (!email) throw firstErr;
        response = await handleApiCall(async () => {
          const res = await api.post({
            url: getApiUrl(endpoint.auth.mfaVerifySetup),
            body: {
              code,
              method,
              email,
              ...(method === 'sms' && phoneNumber ? { phoneNumber } : {}),
            },
            auth: true // Add authentication for MFA verification
          });
          return res;
        });
      }

      // Extract backup codes from response - backend now returns them in data.backupCodes
      const backupCodes = response?.backupCodes || response?.data?.backupCodes || [];

      return {
        statusCode: 200,
        message: 'MFA verified successfully',
        data: response.data || response,
        backupCodes // Include backup codes at top level for easy access
      };
    } catch (error) {

      throw error;
    }
  },

  // Send verification email
  sendVerificationEmail: async (email: string): Promise<any> => {
    try {
      // Get userId from session storage
      const userId = sessionStorage.getItem('signup.userId');

      const result = await handleApiCall(async () => {
        const response = await api.post({
          url: getApiUrl(endpoint.communication.sendVerification),
          body: { email, userId }, // Include userId for proper email verification
          auth: true // Add authentication for verification email
        });
        return response;
      });

      // Only treat this as a failure when the server actually reported one.
      // Previously we threw on anything that didn't have `statusCode === 200`,
      // which triggered a false-positive "Failed to send verification email"
      // toast whenever the API wrapper unwrapped the response (stripping the
      // statusCode field) OR when a retry returned a non-body response. The
      // email was actually sent successfully — users saw an error toast AND
      // then found the verification email in their inbox. Confusing.
      //
      // New rule: trust the backend's contract. It returns
      //   { statusCode: 200, message, data, timestamp }
      // on success and throws (caught by handleApiCall → surfaces as an
      // exception in this try block) on failure. So if we got here with a
      // truthy `result`, it succeeded. Only flag as error when `statusCode`
      // is explicitly an error code (>= 400). Missing statusCode means the
      // wrapper unwrapped it — also fine.
      const explicitFailure =
        result && typeof result.statusCode === 'number' && result.statusCode >= 400;
      if (explicitFailure) {
        const errorMessage = result.message || 'Failed to send verification email';
        console.error('Verification email failed:', {
          statusCode: result.statusCode,
          message: errorMessage,
          errorData: result.data || {},
          timestamp: new Date().toISOString(),
        });
        throw new Error(`Verification email failed: ${errorMessage}`);
      }

      return result;
    } catch (error) {
      // Enhanced error handling with detailed logging
      console.error('Verification email service error:', {
        error: error,
        timestamp: new Date().toISOString(),
        stack: error instanceof Error ? error.stack : 'No stack trace'
      });

      // Extract and format error message for frontend
      let errorMessage = 'Failed to send verification email';
      if (error instanceof Error) {
        errorMessage = error.message;
      } else if (typeof error === 'string') {
        errorMessage = error;
      } else if (error && typeof error === 'object' && 'message' in error) {
        errorMessage = (error as { message: string }).message;
      }

      // Throw formatted error
      throw new Error(errorMessage);
    }
  },

  // Check approval status
  checkApprovalStatus: async (userId: string): Promise<any> => {
    try {
      const result = await handleApiCall(async () => {
        const response = await api.get({
          url: getApiUrl(endpoint.auth.approvalStatus(userId)),
          auth: true // Add authentication for approval status check
        });
        return response;
      });
      return result;
    } catch (error) {

      throw error;
    }
  },

  // Create Stripe SetupIntent to vault payment method
  createSetupIntent: async (): Promise<any> => {
    try {
      const result = await handleApiCall(async () => {
        const response = await api.post({
          url: getApiUrl(endpoint.communication.setupIntents),
          auth: true // Add authentication for setup intent creation
        });
        return response;
      });
      return result;
    } catch (error) {

      throw error;
    }
  },

  // Save payment method to user profile
  savePaymentMethod: async (paymentMethodId: string): Promise<any> => {
    try {
      const result = await handleApiCall(async () => {
        const response = await api.post({
          url: getApiUrl(endpoint.communication.savePaymentMethod),
          body: { paymentMethodId }
        });
        return response;
      });
      return result;
    } catch (error) {

      throw error;
    }
  },

  register: async (body: SignupRequestBody): Promise<SignupResponseBody> => {
    // Use request deduplication to prevent duplicate signup attempts
    const deduplicationKey = `signup-${body.email.toLowerCase()}-${Date.now().toString().slice(0, -3)}000`; // Round to nearest second
    
    return await deduplicateRequest(
      deduplicationKey,
      async () => {
        try {
      // Validate required fields first
      if (!body.email?.trim()) {
        throw new Error('Email is required');
      }
      if (!body.password?.trim() && !body.socialProvider) {
        throw new Error('Password is required for non-social signup');
      }
      if (!body.name?.trim()) {
        throw new Error('Name is required');
      }
      if (!body.selectedPlan) {
        throw new Error('Plan selection is required');
      }

      // Backend now supports both field naming conventions and state conversion!
      const signupData = {
        // Account info - Backend handles both naming conventions
        email: body.email.trim(),
        password: body.password,
        name: body.name.trim(),
        contact_phone: body.contact_phone?.trim() || '',
        npn: body.npn?.trim(),
        npnState: body.npnState?.trim(), // State where NPN is licensed

        // Social auth
        socialProvider: body.socialProvider,
        socialId: body.socialId,
        
        // Plan selection
        selectedPlan: body.selectedPlan,
        requestedTrialType: body.requestedTrialType,
        
        // Agency setup
        agencyName: body.agencyName,
        agencyDescription: body.agencyDescription,
        agencyContactEmail: body.agencyContactEmail,
        downlineAgentCount: body.downlineAgentCount,
        requestedRole: body.requestedRole,
        
        // Area code - Backend handles state name conversion automatically
        selectedAreaCode: body.selectedAreaCode,
        selectedState: body.selectedState, // Can be full name or code
        
        // Payment and security
        paymentMethodId: body.paymentMethodId,
        turnstileToken: body.turnstileToken,
        
        // Invite data
        ...(body.inviteToken && {
          inviteToken: body.inviteToken,
          isInviteSignup: body.isInviteSignup,
          agencyId: body.agencyId,
        }),
      };

      const response = await handleApiCall<any>(async () => {
        const res = await api.post({
          url: getApiUrl(endpoint.auth.postRegister),
          body: signupData,
          auth: false, // Registration should not require authentication
          options: {
            headers: { 'x-signup-session-id': getSignupSessionId() },
          },
        });
        return res;
      });

      // handleApiCall already extracts the data, so response is the actual backend response
      // The backend returns: { statusCode, message, data: { user, agency, ... } }
      // But handleApiCall extracts just the data part
      // So we need to reconstruct the full response format expected by the signup page
      const result: SignupResponseBody = {
        statusCode: 200, // Success if we got here
        message: response.message || 'Registration successful',
        data: response
      };
    
      // Extract nextSteps from backend response to guide frontend flow
      const nextSteps = response?.data?.nextSteps || response?.nextSteps || 'mfa_setup';
      const hasSupabaseSession = response?.data?.hasSupabaseSession || response?.hasSupabaseSession || false;
    
      // Add flow control information to result for frontend to use
      (result as any).nextSteps = nextSteps;
      (result as any).hasSupabaseSession = hasSupabaseSession;

      // Suppress automatic success toasts for signup flow
      // We handle the flow progression ourselves in the signup page
      // User specifically requested to hide registration success messages
      if (result.message?.toLowerCase().includes('trial started') ||
          result.message?.toLowerCase().includes('user registered successfully') ||
          result.message?.toLowerCase().includes('registration successful') ||
          result.message?.toLowerCase().includes('account created') ||
          result.message?.toLowerCase().includes('signup complete')) {
        // Remove the message to prevent automatic toast
        result.message = '';
      }

      return result;
      
        } catch (error) {

          // Handle specific backend error scenarios
          if (error && typeof error === 'object') {
            const errorObj = error as {
              message?: string;
              response?: {
                status?: number;
                data?: {
                  message?: string;
                  error?: string;
                  wasCreatedPreviously?: boolean;
                  userId?: string;
                };
              };
            };

            const status = errorObj.response?.status;
            const errorData = errorObj.response?.data;
            const errorMessage = errorData?.message || errorData?.error || errorObj.message || '';

            // Handle 409 conflicts with structured response
            if (status === 409) {
              // Check for structured 409 response with wasCreatedPreviously flag
              if (errorData?.wasCreatedPreviously ||
                errorMessage.includes('USER_ALREADY_EXISTS') ||
                errorMessage.includes('created successfully') ||
                errorMessage.includes('duplicate request')) {
                // This indicates successful creation from a previous request
                return {
                  statusCode: 200,
                  message: 'User was created successfully',
                  wasAlreadyCreated: true,
                  userId: errorData?.userId
                };
              }
              // For actual duplicate emails, throw the error as-is
              throw error;
            }

            // Handle validation errors (400, 422)
            if (status === 400 || status === 422) {
              throw error;
            }

            // Handle duplicate user ID errors (500 with specific message)
            if (status === 500 && errorMessage.includes('already exists') && errorMessage.includes('id')) {
              throw new Error('Account creation is in progress. Please wait a moment and try again.');
            }
          }

          // If this is a validation or known error, throw it as-is
          if (error instanceof Error) {
            throw error;
          }
          // For unknown errors, wrap in a generic message
          throw new Error('An unexpected error occurred during signup');
        }
      },
      15000 // 15 second TTL for signup requests
    );
  },
};

export default signupService;

